/**
 * Turns what the phone is holding into the one file that leaves it.
 *
 * Everything here is arithmetic on plain values, with no browser in sight, so
 * it can be tested properly. Anything that needs a camera, a screen or a
 * database lives elsewhere on purpose: this is the part where a silent mistake
 * would lose a day of walking.
 */
import type { Photo, SurveyBundle, SurveyPoint } from "./types.js";
import { buildZip, zipSize, type ZipEntry } from "./zip.js";

/**
 * Bumped whenever the shape of `avisos.json` changes.
 *
 * 1 → 2: a point now carries the answers to its tag's questions (`fields`),
 * more than one photo (`photos` instead of `photo`), and the two numbers that
 * say how much the position is worth (`spreadM`, `samples`).
 */
export const FORMAT = 2;
/** Written into the file so a reader knows which tool produced it. */
export const APP = "avisos-camino-app";

function pad(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

/**
 * The moment, written with the phone's own offset rather than in UTC.
 *
 * Whoever reads this later wants to know it was half past seven in the evening
 * on the Meseta, not half past five somewhere abstract. The offset is kept, so
 * nothing is lost either way.
 */
export function localIso(at: Date): string {
  const offsetMin = -at.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
  return `${day}T${time}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * The identifier of a point: the instant it was pinned.
 *
 * It sorts chronologically as plain text, it is safe as a file name on every
 * system, and two points pinned in the same millisecond would need a third
 * hand. No counter to keep, and nothing to collide when two phones are used.
 */
export function pointId(at: Date): string {
  const day = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `${day}-${time}-${pad(at.getMilliseconds(), 3)}`;
}

/**
 * Where a point's photo sits inside the zip.
 *
 * Numbered from one, and always numbered even when there is only one, so that
 * adding a second photo later never renames the first. A photo of the stamp
 * gets `-sello-` in its name instead of joining the plain numbering — that is
 * the only place a photo's kind is recorded; see `Photo` in `types.ts`.
 */
export function photoPath(id: string, index: number, kind: Photo["kind"] = "general"): string {
  return kind === "sello" ? `fotos/${id}-sello-${index + 1}.jpg` : `fotos/${id}-${index + 1}.jpg`;
}

/** What the exported file is called. */
export function exportFileName(at: Date): string {
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `avisos-camino-${day}-${pad(at.getHours())}${pad(at.getMinutes())}.zip`;
}

/** One photo's bytes, already reduced, with what it is a photo of. */
export interface ExportPhoto {
  bytes: Uint8Array;
  kind: Photo["kind"];
}

/** Photo metadata used to preview an export without copying its bytes. */
export interface ExportPreviewPhoto {
  kind: Photo["kind"];
  bytes: number;
}

/** A point with photo sizes, sufficient for an exact ZIP-size preview. */
export interface ExportPreviewItem {
  point: Omit<SurveyPoint, "photos">;
  photos: readonly ExportPreviewPhoto[];
}

/** A point plus its already-reduced photos, ready to be packed. */
export interface ExportItem {
  point: Omit<SurveyPoint, "photos">;
  photos: readonly ExportPhoto[];
}

export interface ExportResult {
  /** Suggested file name. */
  name: string;
  /** What went into `avisos.json`, handy for checking without unzipping. */
  bundle: SurveyBundle;
  zip: Uint8Array<ArrayBuffer>;
}

/**
 * A note inside the archive, for whoever opens it in two years.
 *
 * It costs half a kilobyte and it is the difference between a folder of photos
 * that means something and one that does not.
 */
function readme(bundle: SurveyBundle): string {
  return [
    "Puntos apuntados a pie por el Camino con la app «Avisos del Camino».",
    "",
    `Fecha de exportación: ${bundle.generatedAt}`,
    `Puntos: ${bundle.count}`,
    `Formato: ${bundle.format}`,
    "",
    "avisos.json  — el dato: qué es cada punto, dónde está y cuánto vale eso.",
    "fotos/       — las fotos. El nombre es el del punto y su número de orden.",
    "               Las del sello de la credencial llevan «-sello-» en el",
    "               nombre en vez del número a secas.",
    "",
    "LO QUE HAY QUE MIRAR ANTES DE USAR UNA COORDENADA",
    "",
    "  accuracyM  los metros de error que decía el móvil.",
    "  spreadM    lo lejos que quedaron entre sí las lecturas de verdad.",
    "  samples    cuántas lecturas se promediaron.",
    "",
    "Manda el peor de los dos primeros. Un móvil puede decir «cinco metros» y",
    "estar tirando lecturas a cuarenta: eso solo lo delata «spreadM». Y con",
    "menos de tres lecturas un punto no se puede dar por bueno, porque no hay",
    "con qué comparar.",
    "",
    "Los puntos marcados «perishable» caducan: un puente cortado se arregla y",
    "un perro suelto se ata. No se publican sin mirar la fecha.",
    "",
    "Las fotos van reducidas y sin los datos que traía el fichero original, así",
    "que no llevan dentro dónde estaba quien las hizo. La coordenada buena es",
    "la de avisos.json.",
    "",
  ].join("\n");
}

/** Numbers each photo among others of the same kind, 0-based. */
function indexWithinKind(photos: readonly ExportPhoto[], at: number): number {
  const kind = photos[at]?.kind;
  return photos.slice(0, at).filter((photo) => photo.kind === kind).length;
}

function previewIndexWithinKind(photos: readonly ExportPreviewPhoto[], at: number): number {
  const kind = photos[at]?.kind;
  return photos.slice(0, at).filter((photo) => photo.kind === kind).length;
}

/**
 * Calculates the exact size of the ZIP without allocating a second copy of
 * every photo. The archive deliberately stores JPEGs as-is, so byte lengths
 * are enough to reproduce the final size exactly.
 */
export function estimateExportSize(
  items: readonly ExportPreviewItem[],
  generatedAt: Date,
): number {
  const points: SurveyPoint[] = items.map(({ point, photos }) => ({
    ...point,
    photos: photos.map((photo, at) =>
      photoPath(point.id, previewIndexWithinKind(photos, at), photo.kind),
    ),
  }));
  const bundle: SurveyBundle = {
    format: FORMAT,
    app: APP,
    generatedAt: localIso(generatedAt),
    count: points.length,
    points,
  };
  const encoder = new TextEncoder();
  const entries: Array<{ name: string; size: number }> = [
    { name: "LEEME.txt", size: encoder.encode(readme(bundle)).length },
    { name: "avisos.json", size: encoder.encode(JSON.stringify(bundle, null, 2)).length },
  ];
  for (const { point, photos } of items) {
    photos.forEach((photo, at) => {
      entries.push({
        name: photoPath(point.id, previewIndexWithinKind(photos, at), photo.kind),
        size: photo.bytes,
      });
    });
  }
  return zipSize(entries);
}

/** Packs everything into the single file that comes off the phone. */
export function buildExport(items: readonly ExportItem[], generatedAt: Date): ExportResult {
  const points: SurveyPoint[] = items.map(({ point, photos }) => ({
    ...point,
    photos: photos.map((photo, at) => photoPath(point.id, indexWithinKind(photos, at), photo.kind)),
  }));

  const bundle: SurveyBundle = {
    format: FORMAT,
    app: APP,
    generatedAt: localIso(generatedAt),
    count: points.length,
    points,
  };

  const encoder = new TextEncoder();
  const entries: ZipEntry[] = [
    { name: "LEEME.txt", data: encoder.encode(readme(bundle)), at: generatedAt },
    { name: "avisos.json", data: encoder.encode(JSON.stringify(bundle, null, 2)), at: generatedAt },
  ];
  for (const { point, photos } of items) {
    photos.forEach((photo, at) => {
      const name = photoPath(point.id, indexWithinKind(photos, at), photo.kind);
      entries.push({ name, data: photo.bytes, at: new Date(point.takenAt) });
    });
  }

  return { name: exportFileName(generatedAt), bundle, zip: buildZip(entries) };
}
